import { ReactNode, useCallback, useEffect, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"

import { APIError, useAuth } from "../../../../app/modules/auth"
import { formatLeadItemDate } from "../../../../_metronic/partials/widgets/debugging/DebugPipedrive"
import {
    PassareAcquaintance,
    PassareLeadDetail as PassareLeadDetailData,
    getPassareAcquaintanceByUniqueIdentifier,
} from "../../../core/_requests"
import MogulHeader from "../../../components/MogulHeader"

//
// Helpers
//

function acquaintanceName(a: PassareAcquaintance): string {
    const r = a.public_data.acquaintance_result
    if (r?.display_name) {
        return r.display_name
    }
    if (r) {
        return [r.first_name, r.last_name].filter(Boolean).join(" ") || "Unknown Lead"
    }
    return "Unknown Lead"
}

function acquaintanceAddress(a: PassareAcquaintance): string | null {
    const ad = a.public_data.address
    if (!ad) {
        return null
    }
    const street = [ad.address1, ad.address2, ad.address3, ad.address4].filter(Boolean).join(", ")
    const cityState = [ad.city, ad.state].filter(Boolean).join(", ")
    const full = [street, cityState, ad.zip].filter(Boolean).join(" ")
    return full || null
}

function ghlContactLink(a: PassareAcquaintance): string | null {
    const ghl = a.private_data.ghl_contact
    if (!ghl?.ghl_contact_id) {
        return null
    }
    return `https://app.premier360platform.com/v2/location/${ghl.ghl_location_id}/contacts/detail/${ghl.ghl_contact_id}`
}

function formatPlainDate(value?: string | null): string | null {
    if (!value) {
        return null
    }
    const datePart = value.split("T")[0]
    const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (!match) {
        return value
    }
    const [, year, month, day] = match
    const dt = new Date(Number(year), Number(month) - 1, Number(day))
    if (isNaN(dt.getTime())) {
        return value
    }
    return dt.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
}

//
// Presentational building blocks (same pattern as PassareCaseDetail)
//

function Field({ label, children, col = "col-md-6" }: { label: string; children?: ReactNode; col?: string }) {
    const empty = children === null || children === undefined || children === ""
    return (
        <div className={col}>
            <div className="text-muted fs-8 fw-semibold text-uppercase mb-1">{label}</div>
            <div className="fs-6 fw-bold text-gray-800">
                {empty ? <span className="text-muted fw-normal">—</span> : children}
            </div>
        </div>
    )
}

function SectionCard({
    title,
    children,
    headerRight,
}: {
    title: string
    children: ReactNode
    headerRight?: ReactNode
}) {
    return (
        <div className="card mb-5 shadow-sm">
            <div className="card-header align-items-center">
                <h3 className="card-title fw-bold text-gray-800">{title}</h3>
                {headerRight && <div className="card-toolbar">{headerRight}</div>}
            </div>
            <div className="card-body">{children}</div>
        </div>
    )
}

// One raw incoming webhook. The full payload is admin-gated (it can carry PII),
// matching how PassareCaseDetail gates its raw Passare payload.
function WebhookCard({ webhook, isAdmin, defaultOpen }: { webhook: any; isAdmin: boolean; defaultOpen?: boolean }) {
    const [open, setOpen] = useState(Boolean(defaultOpen))
    return (
        <div className="border border-gray-300 rounded mb-3">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 p-3 bg-light">
                <div className="d-flex flex-wrap align-items-center gap-2">
                    <span className="badge badge-light-primary">{webhook.trigger_type || "webhook"}</span>
                    {webhook.event_id && (
                        <span className="badge badge-light text-muted fs-8">event {webhook.event_id}</span>
                    )}
                    <span className="text-muted fs-7">
                        {webhook.inserted_at ? formatLeadItemDate(webhook.inserted_at) : ""}
                    </span>
                </div>
                {isAdmin && (
                    <button className="btn btn-sm btn-light" onClick={() => setOpen((value) => !value)}>
                        {open ? "Hide payload" : "Show payload"}
                    </button>
                )}
            </div>
            {isAdmin && open && (
                <pre className="bg-light p-4 text-gray-700 mb-0" style={{ maxHeight: 500, overflow: "auto" }}>
                    {JSON.stringify(
                        {
                            event_id: webhook.event_id,
                            trigger_type: webhook.trigger_type,
                            request_information: webhook.request_information,
                            parsed_payload: webhook.parsed_payload,
                            request_body: webhook.request_body,
                        },
                        null,
                        2
                    )}
                </pre>
            )}
        </div>
    )
}

//
// Main component
//

function PassareLeadDetail() {
    const { passare_acquaintance_unique_identifier: acquaintanceId } = useParams<{
        passare_acquaintance_unique_identifier: string
    }>()

    const { currentUser } = useAuth()
    const navigate = useNavigate()

    const [detail, setDetail] = useState<PassareLeadDetailData | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [showRawLead, setShowRawLead] = useState(false)

    const isAdmin = Boolean(currentUser?.is_admin)

    const load = useCallback(async () => {
        if (!acquaintanceId) {
            setError("Missing lead identifier")
            setLoading(false)
            return
        }
        setLoading(true)
        setError(null)
        try {
            const result = await getPassareAcquaintanceByUniqueIdentifier(acquaintanceId)
            setDetail(result)
        } catch (exception) {
            console.error(exception)
            setError(
                exception instanceof APIError
                    ? exception.message
                    : exception instanceof Error
                        ? exception.message
                        : "Failed to load lead"
            )
        } finally {
            setLoading(false)
        }
    }, [acquaintanceId])

    useEffect(() => {
        load()
    }, [load])

    if (loading) {
        return (
            <div className="d-flex align-items-center justify-content-center py-20">
                <span className="spinner-border text-primary" role="status" />
                <span className="ms-3 text-muted">Loading lead…</span>
            </div>
        )
    }

    if (error || !detail) {
        return (
            <div className="py-10">
                <div className="alert alert-danger" role="alert">
                    {error || "Failed to load lead"}
                </div>
                <div className="d-flex gap-3">
                    <button className="btn btn-primary" onClick={load}>
                        Retry
                    </button>
                    <button className="btn btn-light" onClick={() => navigate(-1)}>
                        Go back
                    </button>
                </div>
            </div>
        )
    }

    const lead = detail.acquaintance
    const webhooks = detail.webhooks
    const result = lead.public_data.acquaintance_result
    const phones = lead.public_data.phone_numbers
    const email = lead.public_data.email_address
    const address = acquaintanceAddress(lead)
    const ghlLink = ghlContactLink(lead)
    const caseItem = lead.case_item
    const decedentName = lead.getDecedentFullName()

    return (
        <div className="position-relative pb-10">
            <MogulHeader title={acquaintanceName(lead)} />

            {/* Sub-header: badges + actions */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-5">
                <div className="d-flex flex-wrap align-items-center gap-2">
                    {result?.relationship && (
                        <span className="badge badge-light-info fs-7">{result.relationship}</span>
                    )}
                    {result?.is_deceased && <span className="badge badge-light-dark fs-7">Deceased</span>}
                    {result?.do_not_contact && <span className="badge badge-light-danger fs-7">Do Not Contact</span>}
                    {ghlLink ? (
                        <span className="badge badge-light-success fs-7">Synced to 360</span>
                    ) : (
                        <span className="badge badge-light fs-8 text-muted">Not synced</span>
                    )}
                    <span className="badge badge-light fs-8 text-muted">{lead.unique_identifier}</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <button className="btn btn-sm btn-light" onClick={() => navigate(-1)}>
                        ← Back
                    </button>
                    {caseItem?.unique_identifier && (
                        <Link
                            className="btn btn-sm btn-light-primary"
                            to={`/passare/cases/${caseItem.unique_identifier}`}
                        >
                            View case
                        </Link>
                    )}
                    {ghlLink && (
                        <a className="btn btn-sm btn-light-success" href={ghlLink} target="_blank" rel="noreferrer">
                            View in FRM
                        </a>
                    )}
                </div>
            </div>

            {/* Lead */}
            <SectionCard title="Lead">
                <div className="row g-5">
                    <Field label="Full Name">{acquaintanceName(lead)}</Field>
                    <Field label="Relationship to Decedent">{result?.relationship}</Field>
                    <Field label="Gender">{result?.gender}</Field>
                    <Field label="Date of Birth">{formatPlainDate(result?.birth_date)}</Field>
                    <Field label="Deceased">{result?.is_deceased ? "Yes" : null}</Field>
                    <Field label="Do Not Contact">{result?.do_not_contact ? "Yes" : null}</Field>
                </div>
            </SectionCard>

            {/* Contact */}
            <SectionCard title="Contact">
                <div className="row g-5">
                    <Field label="Home Phone">{phones?.home_phone}</Field>
                    <Field label="Mobile Phone">{phones?.mobile_phone}</Field>
                    <Field label="Work Phone">{phones?.work_phone}</Field>
                    <Field label="Email">{email}</Field>
                    <Field label="Address" col="col-md-12">
                        {address}
                    </Field>
                </div>
            </SectionCard>

            {/* Related case */}
            <SectionCard title="Related Case">
                <div className="row g-5">
                    <Field label="Decedent">
                        {caseItem?.unique_identifier ? (
                            <Link to={`/passare/cases/${caseItem.unique_identifier}`}>{decedentName}</Link>
                        ) : (
                            decedentName
                        )}
                    </Field>
                    <Field label="Service Type">{caseItem?.public_data?.case_result?.client_service_type_name}</Field>
                    <Field label="Branch">{caseItem?.branch_name}</Field>
                    <Field label="Organization">
                        {lead.passare_organization ? (
                            <Link to={`/passare/organizations/${lead.passare_organization.unique_identifier}`}>
                                {lead.passare_organization.organization_name || "View organization"}
                            </Link>
                        ) : null}
                    </Field>
                </div>
            </SectionCard>

            {/* Webhook data — the entire incoming payload(s) for this lead */}
            <SectionCard
                title="Webhook Data"
                headerRight={
                    <span className="badge badge-light-primary">
                        {webhooks.length} {webhooks.length === 1 ? "webhook" : "webhooks"}
                    </span>
                }
            >
                {webhooks.length === 0 ? (
                    <div className="text-muted">
                        No raw webhook requests are linked to this lead (matched by acquaintance / case UUID).
                    </div>
                ) : (
                    <>
                        {!isAdmin && (
                            <div className="text-muted mb-3 fs-7">
                                Payloads are visible to admins. Showing webhook summaries.
                            </div>
                        )}
                        {webhooks.map((webhook, index) => (
                            <WebhookCard
                                key={webhook.unique_identifier || index}
                                webhook={webhook}
                                isAdmin={isAdmin}
                                defaultOpen={index === 0}
                            />
                        ))}
                    </>
                )}
            </SectionCard>

            {/* Raw lead data (admins) */}
            {isAdmin && (
                <SectionCard
                    title="Raw Lead Data (admin)"
                    headerRight={
                        <button className="btn btn-sm btn-light" onClick={() => setShowRawLead((value) => !value)}>
                            {showRawLead ? "Hide" : "Show"}
                        </button>
                    }
                >
                    {showRawLead ? (
                        <pre
                            className="bg-light p-4 rounded text-gray-700 mb-0"
                            style={{ maxHeight: 500, overflow: "auto" }}
                        >
                            {JSON.stringify({ public_data: lead.public_data, private_data: lead.private_data }, null, 2)}
                        </pre>
                    ) : (
                        <div className="text-muted">Hidden. Click “Show” to inspect the raw lead payload.</div>
                    )}
                </SectionCard>
            )}
        </div>
    )
}

export default PassareLeadDetail
